/**
 * useUnseenChallengeResults
 * N+1 cursor pattern: receives the 6-item history preview (fetched as ARENA_HISTORY_PREVIEW_SIZE = 6).
 * If all 6 are unseen, we know there is overflow without a separate count query.
 * Shows max 5 results; hasOverflow signals the "+more" hint.
 */

import { useState, useMemo, useRef, useCallback } from 'react';
import { parseISO } from 'date-fns';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants';
import type { ChallengeWithStats } from '../../../types/challenges';
import type { GameResultItem } from '../../../components/challenges/GameResultModal/gameResultModal.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import { buildGameResultItem } from '../helpers/challengeResultItem.helper';

interface CurrentUser {
  id: string;
  lastSeenResultsAt?: string | null;
  /** Live profile avatar — when present, used for YOUR side of the modal instead of the snapshot. */
  avatarId?: string | null;
  avatarConfig?: EquippedMap | null;
}

interface UseUnseenChallengeResultsReturn {
  pendingResults: GameResultItem[];
  hasOverflow: boolean;
  markAllSeen: () => void;
}

export function useUnseenChallengeResults(
  latestSixChallenges: ChallengeWithStats[],
  currentUser: CurrentUser | null | undefined,
): UseUnseenChallengeResultsReturn {
  const apiClient = useApiClient();
  const [optimisticLastSeen, setOptimisticLastSeen] = useState<string | null>(null);
  const isDismissing = useRef(false);

  const effectiveLastSeen = optimisticLastSeen ?? currentUser?.lastSeenResultsAt;

  const { pendingResults, hasOverflow } = useMemo(() => {
    if (!latestSixChallenges || !currentUser?.id) {
      return { pendingResults: [], hasOverflow: false };
    }

    const lastSeenMs = effectiveLastSeen ? parseISO(effectiveLastSeen).getTime() : 0;

    // Filter to unseen completed challenges
    const unseen = latestSixChallenges.filter(
      (c) =>
        String(c.status).toUpperCase() === 'COMPLETED' &&
        c.completedAt != null &&
        parseISO(c.completedAt as string).getTime() > lastSeenMs,
    );

    // If all 6 are unseen, there is overflow beyond what we fetched
    const hasOverflow = unseen.length === 6;

    // Show max 5; map to GameResultItem (1v1 only, nulls filtered)
    const liveAvatar = currentUser.avatarId !== undefined
      ? { avatarId: currentUser.avatarId, avatarConfig: currentUser.avatarConfig ?? null }
      : undefined;

    const results = unseen
      .slice(0, 5)
      .map((c) => buildGameResultItem(c, currentUser.id, liveAvatar))
      .filter((r): r is GameResultItem => r !== null);

    return { pendingResults: results, hasOverflow };
  }, [
    latestSixChallenges,
    currentUser?.id,
    currentUser?.avatarId,
    currentUser?.avatarConfig,
    effectiveLastSeen,
  ]);

  const markAllSeen = useCallback(() => {
    if (isDismissing.current) return;
    isDismissing.current = true;

    // Optimistic update — clears pendingResults immediately regardless of network speed
    setOptimisticLastSeen(new Date().toISOString());

    apiClient(API_ENDPOINTS.users.lastSeenResults, { method: 'PATCH' }).catch(() => {
      // Fail silently — forgiving UX: user sees same results again on next launch
    }).finally(() => {
      isDismissing.current = false;
    });
  }, [apiClient]);

  return { pendingResults, hasOverflow, markAllSeen };
}
